/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import cn from 'classnames'

// Components
import SubMenuBanner from './libs/subMenuBanner/SubMenuBanner'
import DesktopMenu from './DesktopMenu'
import MobileMenu from './MobileMenu'
import UserActions from './UserActions'
import ContactNumber from './ContactNumber'
import HeaderActionIcons from './HeaderActionIcons'

// hooks
import { useMediaQuery } from '@utils'
import { HeaderProps } from '@types'
import {
  useNotifications,
  useUser,
  useDiscountBanner,
  useHeaderState,
} from '@hooks'

import s from './Header.module.scss'

const DiscountBanner = dynamic(() => import('@/components/discountBanner/'))
const DynamicEditPasswordModal = dynamic(
  () => import('@/components/editPasswordModal')
)

export default function Header({
  isDenied,
  disabled,
  isDiscountBannerClosed,
  setIsDiscountBannerClosed,
}: HeaderProps) {
  const router = useRouter()
  const isTablet = useMediaQuery('(max-width:1270px)')
  const [isMounted, setIsMounted] = useState(false)

  // Hooks
  const { userData, setUserData } = useUser()
  const { unreadApplications } = useNotifications(userData)
  const {
    state,
    headerDispatch,
    toggleMenu,
    setLoginModal,
    showPasswordModal,
    setShowPasswordModal,
  } = useHeaderState()

  const { hasBanner } = useDiscountBanner(
    Boolean(isDiscountBannerClosed),
    router.pathname
  )

  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Auto-collapse menu on tablet/desktop switch or logout
  useEffect(() => {
    if (!isTablet && !userData && state.showMenu) {
      toggleMenu(false)
    }
  }, [isTablet, userData, state.showMenu, toggleMenu])

  if (disabled) return null

  // Reuse "isOpen" logic from original file
  const isOpen = true

  return (
    <>
      <div
        id="navbar"
        className={cn({
          [s.container]: true,
          [s.hasBanner]: hasBanner || false,
        })}
      >
        {/* password modal */}
        {!showPasswordModal && (
          <DynamicEditPasswordModal
            isOpen={showPasswordModal}
            close={() => setShowPasswordModal(false)}
          />
        )}

        {/* discount banner */}
        {hasBanner && !state.showMenu && (
          <DiscountBanner
            isOpen={isOpen}
            isDiscountBannerClosed={isDiscountBannerClosed}
            setIsDiscountBannerClosed={setIsDiscountBannerClosed}
          />
        )}

        {/* header */}
        <div
          className={cn({
            [s.header]: true,
            [s.isBannerOpen]:
              isOpen && hasBanner && !state.showMenu && isMounted,
          })}
        >
          <div className={s.sections1}>
            {(userData?.type === 'Customer' || !userData) && isMounted ? (
              <>
                <Link href="/">
                  <div
                    className={s.mawooWithoutLogin}
                    role="img"
                    aria-label="Mawoo Pets horizontal logo"
                  />
                </Link>

                {isTablet && isMounted && (
                  <Link href="/puppy-financing" className={s.financing}>
                    <img
                      src="/financings/wallet.svg"
                      alt="Wallet"
                      width={35}
                      height={35}
                    />
                    <span>
                      Financing
                      <br /> Available
                    </span>
                  </Link>
                )}
              </>
            ) : (
              <Link href="/">
                <div className={s.mawoo} role="img" aria-label="Mawoo Pets" />
              </Link>
            )}

            {/* menu -> Desktop Menu */}
            <div className={s.options}>
              <DesktopMenu userData={userData} />
            </div>
          </div>

          {/* buttons */}
          <div className={s.sections2}>
            {/* Desktop Action Icons (Financing, Phone) */}
            <HeaderActionIcons
              userData={userData}
              isTablet={isTablet}
              isMounted={isMounted}
            />

            <div className={s.actions}>
              {/* User Actions (Keypad or Login/Signup) */}
              <UserActions
                userData={userData}
                unreadApplications={unreadApplications}
                isMounted={isMounted}
                showMenu={state.showMenu}
                showLoginModal={state.showLoginModal}
                toggleMenu={() => toggleMenu()}
                setLoginModal={setLoginModal}
              />

              {/* Tablet specific search/phone */}
              <ContactNumber
                isMounted={isMounted}
                className={s.contactNumber}
              />

              {/* Mobile Menu (Burger & Drawer) */}
              <MobileMenu
                showMenu={state.showMenu}
                toggleMenu={() => toggleMenu()}
                userData={userData}
                setUserData={setUserData}
                dispatch={headerDispatch}
                state={state}
              />
            </div>
          </div>
        </div>
      </div>

      <SubMenuBanner disabled={isDenied} userData={userData} />
    </>
  )
}
